import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import { useHasAccountPermission } from '../account-permissions'
import { useObjectActionMutation, useObjectActionQuery, useObjectGet } from '../api/object'
import { Button } from '../components/button'
import { Dialog } from '../components/dialog'
import { usePageLayout } from '../layouts'
import { PageLayout } from '../layouts/page-layout'
import { useToggle } from '../use-toggle'
import { ActionModal } from '../widget/action-modal'
import { Conflicts } from '../widget/conflicts'

type AtdAccessCode = {
	code: string
	hint: string
	length: number
	updated_at: string | null
}

export function Base() {
	const { reservationId = '' } = useParams()
	const { data: reservation, isLoading } = useObjectGet('reservation', reservationId)
	const atdBaseUrl = String(process.env.ATD_PUBLIC_URL ?? '').trim().replace(/\/+$/, '')
	const atdReservationUrl = atdBaseUrl
		? `${atdBaseUrl}/admin/?id=${encodeURIComponent(reservationId)}`
		: null
	const canManageAtdCode = useHasAccountPermission(ACCOUNT_PERMISSION.EDIT_RESERVATION)
	const { mutate: cancel } = useObjectActionMutation('reservation', reservationId, 'cancel')
	const { mutate: dispatch } = useObjectActionMutation('reservation', reservationId, 'dispatch')
	const [finish, toggleFinish] = useToggle()
	const [isCancelling, toggleIsCancelling] = useToggle()
	const queryClient = useQueryClient()

	const { layout: pageLayout } = usePageLayout('reservation')
	const atdAccessCodeQuery = useObjectActionQuery<AtdAccessCode>(
		'reservation',
		reservationId,
		'atd-access-code',
		{
			enabled: canManageAtdCode && !!reservationId,
			staleTime: 30_000,
		},
	)

	const regenerateAtdCodeMutation = useObjectActionMutation(
		'reservation',
		reservationId,
		'atd-access-code/regenerate',
		[['api', 'reservation', reservationId, 'atd-access-code']],
	)

	const regenerateAtdCode = () =>
		regenerateAtdCodeMutation.mutate(undefined, {
			onSuccess(data) {
				queryClient.setQueryData(
					['api', 'reservation', reservationId, 'atd-access-code'],
					data,
				)
			},
		})
	const openAtdReservation = () => {
		if (!reservationId || !atdReservationUrl) {
			return
		}
		window.location.assign(atdReservationUrl)
	}

	if (isLoading) {
		return (
			<section className="container m-auto">
				<div className="flex items-center gap-4">
					<h3>Rezervace</h3>
				</div>
			</section>
		)
	}

	return (
		<section className="container m-auto">
			<div className="flex justify-between gap-4 p-4">
				<div className="space-y-2">
					<h3 className="text-md font-bold">
						Rezervace{' '}
						<span className="text-gray-600 font-normal">ID:{reservation?.id}</span>
					</h3>
					{canManageAtdCode ? (
						<div className="inline-flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
							<span className="font-semibold">ATD user heslo:</span>
							<code className="rounded bg-white px-2 py-0.5 text-xs">
								{atdAccessCodeQuery.data?.code ??
									(atdAccessCodeQuery.isLoading
										? 'načítám…'
										: 'není k dispozici')}
							</code>
							<Button
								variant="SECONDARY"
								size="SMALL"
								isPending={regenerateAtdCodeMutation.isPending}
								onPress={regenerateAtdCode}
							>
								Vygenerovat nové
							</Button>
								{atdReservationUrl ? (
									<Button variant="SECONDARY" size="SMALL" onPress={openAtdReservation}>
										Otevřít ATD
									</Button>
								) : null}
							</div>
						) : null}
				</div>
				{reservation && reservation.status !== 'cancelled' ? (
					<div className="flex gap-3">
						{reservation.status === 'paid' ? (
							<Button variant="PRIMARY" onPress={() => dispatch({})}>
								Vydat auto
							</Button>
						) : null}
						{reservation.status === 'paid' || reservation.status === 'dispatched' ? (
							<Button variant="PRIMARY" onPress={toggleFinish}>
								Ukončit (vratka)
							</Button>
						) : null}
						{finish ? (
							<ActionModal
								action="finish"
								layout={{
									object: 'reservation',
									name: 'finish',
									dependencies: ['expense', 'payment', 'client'],
								}}
								parent={reservation}
								title="Uzavření"
								onClose={toggleFinish}
							/>
						) : null}
						<Button variant="SECONDARY" onPress={toggleIsCancelling}>
							Zrušit
						</Button>
						{isCancelling ? (
							<Dialog
								heading="Zrušit rezervaci"
								isOpen
								onOpenChange={toggleIsCancelling}
							>
								<div className="m-4">
									<p>Opravdu chcete zrušit rezervaci?</p>
								</div>
								<div className="flex justify-between gap-4 m-4">
									<Button
										variant="DANGER"
										onPress={() => {
											cancel(undefined, {
												onSuccess() {
													toggleIsCancelling(false)
												},
											})
										}}
									>
										Ano
									</Button>
									<Button variant="SECONDARY" onPress={toggleIsCancelling}>
										Ne
									</Button>
								</div>
							</Dialog>
						) : null}
					</div>
				) : null}
			</div>
			<Conflicts id={reservationId} />
			{pageLayout ? (
				<PageLayout
					object="reservation"
					id={reservationId}
					data={reservation}
					layout={pageLayout}
				/>
			) : null}
		</section>
	)
}
